import type { FavorabilityPoint } from '@/types/conditions';
import type { ObservedSighting } from '@/types/patterns';

/**
 * ConditionSparkline — shared presentational sparkline (DASH-T3 / M90).
 *
 * Plots a per-step favorability curve across the TIME-OF-DAY span of `points`
 * (clock component since local midnight; calendar date discarded), overlays
 * observed-sighting glyphs at a constant x-axis baseline, and optionally a
 * now-marker. The component is deliberately TIER-AGNOSTIC: the curve stroke is
 * a single static token and is never branched on score/tier. Marker geometry —
 * not color — carries the favorability signal.
 *
 * Leaf-array contract: this component takes `FavorabilityPoint[]` and
 * `ObservedSighting[]` leaf arrays directly — never the overlay wrapper type.
 *
 * Self-contained (Principle 16): all SVG scaling/positioning math lives here.
 * No shared sparkline helper is imported or extracted.
 */

interface ConditionSparklineProps {
  points: FavorabilityPoint[];
  sightings?: ObservedSighting[];
  now?: Date;
}

// STATIC geometry — never derived from props.
const WIDTH = 280;
const HEIGHT = 48;
const CURVE_TOP_PADDING = 4;
const CURVE_BOTTOM_PADDING = 12; // leaves room for the sighting baseline below the curve
const BASELINE_Y = HEIGHT - 4; // constant x-axis floor for sighting glyphs (score-independent)
const SIGHTING_RADIUS = 3;
const NOW_MARKER_WIDTH = 1;
// STATIC curve stroke — single favorable token, never score-conditional and
// never tier-derived. Marker geometry (not color) carries the favorability signal.
const CURVE_STROKE = 'var(--color-favorable)';

const MS_PER_HOUR = 3_600_000;

/** Milliseconds elapsed since local midnight for an ISO timestamp — the clock
 *  component only; the calendar date is intentionally discarded. */
function timeOfDayMs(iso: string): number {
  const d = new Date(iso);
  return (
    d.getHours() * MS_PER_HOUR +
    d.getMinutes() * 60_000 +
    d.getSeconds() * 1_000 +
    d.getMilliseconds()
  );
}

/** Milliseconds since local midnight for a Date (now-marker). */
function timeOfDayMsFromDate(d: Date): number {
  return (
    d.getHours() * MS_PER_HOUR +
    d.getMinutes() * 60_000 +
    d.getSeconds() * 1_000 +
    d.getMilliseconds()
  );
}

export function ConditionSparkline({
  points,
  sightings,
  now,
}: ConditionSparklineProps) {
  // Empty/absent guard — render NOTHING (FC1 far-day mount safety).
  if (!points || points.length === 0) {
    return null;
  }

  // DYNAMIC: time-of-day domain bounds across the points.
  const todValues = points.map((p) => timeOfDayMs(p.t));
  const domainMin = Math.min(...todValues);
  const domainMax = Math.max(...todValues);
  const span = domainMax - domainMin;

  // Map a time-of-day (ms) to an x coordinate within [0, WIDTH].
  // When the domain has zero span (single/identical points), collapse to x=0.
  const xForTod = (tod: number): number =>
    span === 0 ? 0 : ((tod - domainMin) / span) * WIDTH;

  // DYNAMIC: curve points by time-of-day x and score-derived y.
  const curvePoints = points
    .map((p) => {
      const x = xForTod(timeOfDayMs(p.t));
      const usableHeight = HEIGHT - CURVE_TOP_PADDING - CURVE_BOTTOM_PADDING;
      const y =
        HEIGHT -
        CURVE_BOTTOM_PADDING -
        (Math.max(0, Math.min(100, p.score)) / 100) * usableHeight;
      return `${x.toFixed(2)},${y.toFixed(2)}`;
    })
    .join(' ');

  // DYNAMIC: sighting glyphs. Exclude undefined-time sightings (their count is
  // W1's concern). Exclude timed sightings whose time-of-day is outside the
  // domain — NOT clamped, NOT pinned to an edge: simply not plotted.
  const plottedSightings = (sightings ?? [])
    .filter((s): s is ObservedSighting & { time: string } => s.time != null)
    .map((s) => ({ sighting: s, tod: timeOfDayMs(s.time) }))
    .filter(({ tod }) => tod >= domainMin && tod <= domainMax)
    .map(({ sighting, tod }) => ({ sighting, x: xForTod(tod) }));

  // DYNAMIC: now-marker only when `now` is provided AND in-domain.
  let nowX: number | null = null;
  if (now) {
    const nowTod = timeOfDayMsFromDate(now);
    if (nowTod >= domainMin && nowTod <= domainMax) {
      nowX = xForTod(nowTod);
    }
  }

  return (
    <svg
      data-testid="condition-sparkline"
      width="100%"
      height={HEIGHT}
      viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
      preserveAspectRatio="none"
      role="img"
      aria-label="Favorability across the day with observed sightings"
    >
      <polyline
        data-testid="sparkline-curve"
        points={curvePoints}
        fill="none"
        stroke={CURVE_STROKE}
        strokeWidth="1.5"
        strokeLinejoin="round"
        strokeLinecap="round"
      />

      {nowX !== null && (
        <line
          data-testid="sparkline-now-marker"
          data-x={nowX.toFixed(2)}
          x1={nowX}
          x2={nowX}
          y1={CURVE_TOP_PADDING}
          y2={BASELINE_Y}
          stroke="var(--color-text-placeholder)"
          strokeWidth={NOW_MARKER_WIDTH}
        />
      )}

      {plottedSightings.map(({ sighting, x }) => {
        const sexLabel = sighting.animalSex ?? 'unknown';
        const countLabel =
          sighting.animalCount != null ? ` (${sighting.animalCount})` : '';
        const behaviorLabel = sighting.animalBehavior
          ? ` — ${sighting.animalBehavior}`
          : '';
        // Reflective/past-tense only — never predictive.
        const label = `observed ${sexLabel}${countLabel}${behaviorLabel}`;
        return (
          <circle
            key={sighting.entryId}
            data-testid="sparkline-sighting"
            data-x={x.toFixed(2)}
            cx={x}
            cy={BASELINE_Y}
            r={SIGHTING_RADIUS}
            fill="var(--color-favorable)"
            aria-label={label}
          >
            <title>{label}</title>
          </circle>
        );
      })}
    </svg>
  );
}
